// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus"

// Survey Assignment Management Controller
// Handles dynamic addition/removal of survey assignments in admin forms
// Also manages role-based visibility of assignments section
export default class extends Controller {
  static targets = ["table", "assignmentsSection", "nonAssignableMessage"]
  static values = {
    nextIndex: Number
  }

  connect() {
    console.log("Survey assignments controller connected")
    // Initialize next index based on existing rows
    this.updateNextIndex()

    // Load survey data asynchronously
    this.loadSurveyData()

    // Set up role change handling
    this.setupRoleChangeHandler()

    // Set initial visibility based on current role
    this.updateAssignmentVisibility()

    // Update survey options to disable already selected surveys
    setTimeout(() => this.updateSurveyOptions(), 100) // Small delay to ensure DOM is ready
  }

  disconnect() {
    if (this.roleSelect) {
      this.roleSelect.removeEventListener('change', this.handleRoleChange.bind(this))
    }
  }

  // Set up role change handling
  setupRoleChangeHandler() {
    this.roleSelect = this.element.closest('form')?.querySelector('select[name*="[role]"]')
    if (this.roleSelect) {
      this.roleSelect.addEventListener('change', this.handleRoleChange.bind(this))
    }
  }

  // Handle role change events
  handleRoleChange(event) {
    this.updateAssignmentVisibility()
  }

  // Update visibility based on selected role
  updateAssignmentVisibility() {
    const selectedRole = this.roleSelect?.value || 'assignable'
    const isAssignable = selectedRole === 'assignable'

    console.log(`Role changed to: ${selectedRole}, showing assignments: ${isAssignable}`)

    // Show/hide assignments section
    if (this.hasAssignmentsSectionTarget) {
      this.assignmentsSectionTarget.style.display = isAssignable ? 'block' : 'none'
    }

    // Show/hide non-assignable message
    if (this.hasNonAssignableMessageTarget) {
      this.nonAssignableMessageTarget.style.display = isAssignable ? 'none' : 'block'
    }

    // Update submit button text if present
    const submitButton = this.element.closest('form')?.querySelector('input[type="submit"]')
    if (submitButton) {
      submitButton.value = isAssignable ?
        'Update Admin & Survey Assignments' :
        'Update Admin'
    }
  }

  // Load survey data from server to avoid large HTML payloads
  async loadSurveyData() {
    try {
      const response = await fetch('/admin_users/survey_options')
      if (response.ok) {
        this.surveysByClient = await response.json()
        console.log("Survey data loaded:", Object.keys(this.surveysByClient).length, "clients")
      } else {
        console.warn("Failed to load survey data, using fallback")
        this.surveysByClient = {}
      }
    } catch (error) {
      console.warn("Error loading survey data:", error)
      this.surveysByClient = {}
    }
  }

  // Add a new survey assignment row
  addAssignment() {
    const table = this.tableTarget
    const tbody = table.querySelector('tbody') || table

    // Create new row with unique index
    const newIndex = this.nextIndexValue
    const newRow = this.createAssignmentRow(newIndex)

    tbody.appendChild(newRow)
    this.nextIndexValue = newIndex + 1

    // Focus on the survey select
    const surveySelect = newRow.querySelector('select[name*="survey_id"]')
    if (surveySelect) {
      surveySelect.focus()
    }

    // Update survey options to disable already selected surveys
    this.updateSurveyOptions()
  }

  // Remove a single assignment row
  removeAssignment(event) {
    const row = event.target.closest('tr')
    const destroyField = row.querySelector('input[name*="_destroy"]')

    if (destroyField) {
      // Mark for destruction if persisted
      destroyField.value = '1'
      row.style.display = 'none'
      row.classList.add('marked-for-destruction')
    } else {
      // Remove from DOM if not persisted
      row.remove()
    }

    // Update survey options to re-enable the survey that was just removed
    this.updateSurveyOptions()
  }

  // Remove selected assignments (bulk operation)
  removeSelected() {
    const checkboxes = this.element.querySelectorAll('.assignment-checkbox:checked')

    checkboxes.forEach(checkbox => {
      const row = checkbox.closest('tr')
      const destroyField = row.querySelector('input[name*="_destroy"]')

      if (destroyField) {
        // Mark for destruction if persisted
        destroyField.value = '1'
        row.style.display = 'none'
        row.classList.add('marked-for-destruction')
      } else {
        // Remove from DOM if not persisted
        row.remove()
      }
    })

    // Update survey options to re-enable surveys that were just removed
    this.updateSurveyOptions()
  }

  // Toggle all checkboxes
  toggleAll(event) {
    const isChecked = event.target.checked
    const checkboxes = this.element.querySelectorAll('.assignment-checkbox')

    checkboxes.forEach(checkbox => {
      // Only toggle visible rows
      const row = checkbox.closest('tr')
      if (row.style.display !== 'none') {
        checkbox.checked = isChecked
      }
    })
  }

  // Update client name when survey selection changes
  updateClient(event) {
    const surveySelect = event.target
    const clientCell = surveySelect.closest('tr').querySelector('.survey-client')

    if (surveySelect.value && this.surveysByClient) {
      // Find the client name for the selected survey
      let clientName = '-'

      for (const [client, surveys] of Object.entries(this.surveysByClient)) {
        if (surveys.some(survey => survey.id.toString() === surveySelect.value)) {
          clientName = client
          break
        }
      }

      clientCell.textContent = clientName
    } else {
      clientCell.textContent = '-'
    }

    // Update survey options to disable already selected surveys
    this.updateSurveyOptions()
  }

  // Update all survey selects to disable already selected options
  updateSurveyOptions() {
    const surveySelects = this.element.querySelectorAll('select[name*="[survey_id]"]')
    const selectedValues = new Set()

    // Collect all currently selected survey IDs (excluding empty values)
    surveySelects.forEach(select => {
      const row = select.closest('tr')
      // Skip rows marked for destruction
      if (select.value && !row.classList.contains('marked-for-destruction')) {
        selectedValues.add(select.value)
      }
    })

    // Update each select to disable already selected options
    surveySelects.forEach(select => {
      const currentValue = select.value
      const options = select.querySelectorAll('option')

      options.forEach(option => {
        if (option.value && option.value !== currentValue) {
          option.disabled = selectedValues.has(option.value)
        }
      })
    })
  }

  private

  // Create a new assignment row
  createAssignmentRow(index) {
    const row = document.createElement('tr')
    row.className = 'assignment-row'

    // Build survey options with client optgroups from loaded data
    let surveyOptionsHTML = '<option value="">Select a survey...</option>'

    try {
      if (this.surveysByClient && Object.keys(this.surveysByClient).length > 0) {
        const optgroups = Object.entries(this.surveysByClient).map(([clientName, surveys]) => {
          const options = surveys.map(survey => {
            return `<option value="${survey.id}">${survey.name}</option>`
          }).join('')

          return `<optgroup label="${clientName}">${options}</optgroup>`
        }).join('')

        surveyOptionsHTML += optgroups
      }
    } catch (error) {
      console.warn('Could not load survey options from data:', error)
      // Fallback: copy options from existing survey selects
      const existingSelect = this.element.querySelector('select[name*="[survey_id]"]')
      if (existingSelect && existingSelect.options.length > 1) {
        const options = Array.from(existingSelect.options).map(option => {
          if (option.value) {
            return `<option value="${option.value}">${option.text}</option>`
          }
          return ''
        }).join('')
        surveyOptionsHTML = '<option value="">Select a survey...</option>' + options
      }
    }

    row.innerHTML = `
      <td>
        <select class="form-control" required name="admin[admin_survey_assignments_attributes][${index}][survey_id]" 
                data-action="change->survey-assignments#updateClient">
          ${surveyOptionsHTML}
        </select>
      </td>
      <td class="survey-client">-</td>
      <td>
        <select class="form-control" required name="admin[admin_survey_assignments_attributes][${index}][role]">
          <option value="client_monitor">Client Monitor</option>
          <option value="moderator" selected>Moderator</option>
          <option value="field_admin">Field Admin</option>
        </select>
      </td>
      <td>
        <button type="button" class="btn btn-sm btn-outline-danger" 
                data-action="click->survey-assignments#removeAssignment">
          <i class="fa fa-trash"></i>
        </button>
      </td>
      <td>
        <input type="checkbox" class="assignment-checkbox" />
      </td>
    `
    return row
  }

  // Update the next index based on existing rows
  updateNextIndex() {
    const existingInputs = this.element.querySelectorAll('input[name*="admin_survey_assignments_attributes"]')
    let maxIndex = Date.now() // Use timestamp as base

    existingInputs.forEach(input => {
      const match = input.name.match(/\[(\d+)\]/)
      if (match) {
        const index = parseInt(match[1])
        if (index > maxIndex) {
          maxIndex = index
        }
      }
    })

    this.nextIndexValue = maxIndex + 1
  }
}
